// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
//
// Pure seismology model. No DOM, no fetch — everything here is unit-tested.
import type { Quake } from './types';

/**
 * Seismic energy radiated by an earthquake, in joules, from the Gutenberg–Richter
 * energy–magnitude relation log10(E) = 1.5·M + 4.8. A one-unit magnitude step is
 * therefore ~31.6× more energy.
 */
export function energyJoules(mag: number): number {
  if (!Number.isFinite(mag)) return 0;
  return Math.pow(10, 1.5 * mag + 4.8);
}

export function totalEnergy(quakes: Quake[]): number {
  let sum = 0;
  for (const q of quakes) sum += energyJoules(q.mag);
  return sum;
}

/** Share (0–1) of total radiated energy contributed by the single largest event. */
export function energyConcentration(quakes: Quake[]): number {
  if (quakes.length === 0) return 0;
  const total = totalEnergy(quakes);
  if (total <= 0) return 0;
  let maxE = 0;
  for (const q of quakes) maxE = Math.max(maxE, energyJoules(q.mag));
  return maxE / total;
}

export interface MagClass {
  label: string;
  min: number;
}

const MAG_CLASSES: MagClass[] = [
  { label: 'Great', min: 8 },
  { label: 'Major', min: 7 },
  { label: 'Strong', min: 6 },
  { label: 'Moderate', min: 5 },
  { label: 'Light', min: 4 },
  { label: 'Minor', min: 2 },
  { label: 'Micro', min: -Infinity },
];

/** USGS-style class name for a magnitude (Micro/Minor/Light/…/Great). */
export function magClass(mag: number): string {
  if (!Number.isFinite(mag)) return 'Unknown';
  for (const c of MAG_CLASSES) if (mag >= c.min) return c.label;
  return 'Micro';
}

/** Fixed magnitude colour ramp — same colour for the same size in every view. */
export function magColor(mag: number): string {
  if (!Number.isFinite(mag)) return '#64748b';
  if (mag >= 8) return '#c084fc'; // Great — violet
  if (mag >= 7) return '#ef4444'; // Major — red
  if (mag >= 6) return '#f97316'; // Strong — dark orange
  if (mag >= 5) return '#fb923c'; // Moderate — orange
  if (mag >= 4) return '#facc15'; // Light — yellow
  if (mag >= 3) return '#a3e635'; // upper Minor — lime
  if (mag >= 2) return '#4ade80'; // lower Minor — green
  return '#22d3ee'; // Micro — cyan
}

export type DepthCat = 'Shallow' | 'Intermediate' | 'Deep';

/** Standard seismology depth bands (km): shallow <70, intermediate 70–300, deep >300. */
export function depthCategory(depth: number): DepthCat {
  if (!Number.isFinite(depth)) return 'Shallow';
  if (depth >= 300) return 'Deep';
  if (depth >= 70) return 'Intermediate';
  return 'Shallow';
}

/** Depth colour ramp — shallow = warm (more damaging), deep = cool. */
export function depthColor(depth: number): string {
  if (!Number.isFinite(depth)) return '#64748b';
  if (depth < 35) return '#f87171'; // very shallow — red
  if (depth < 70) return '#fb923c'; // shallow — orange
  if (depth < 150) return '#fbbf24'; // upper intermediate — amber
  if (depth < 300) return '#38bdf8'; // lower intermediate — light blue
  return '#6366f1'; // deep — indigo
}

/**
 * Plain-language "would you feel this / could it do harm" line for a magnitude.
 * Deliberately measured — this describes typical effects near the epicentre;
 * actual shaking depends on depth, distance and local geology.
 */
export function feltVerdict(mag: number): string {
  if (!Number.isFinite(mag)) return 'Magnitude not yet determined.';
  if (mag >= 8) return 'Great earthquake — can cause total destruction near the epicentre and is felt across enormous distances.';
  if (mag >= 7) return 'Major earthquake — serious damage over large areas.';
  if (mag >= 6) return 'Strong — can be destructive in populated areas up to ~100 km across.';
  if (mag >= 5) return 'Moderate — felt widely; can slightly damage well-built buildings and more so weaker ones.';
  if (mag >= 4) return 'Light — noticeable indoor shaking, rattling objects; significant damage unlikely.';
  if (mag >= 3) return 'Minor — often felt by people nearby, but rarely causes damage.';
  if (mag >= 2) return 'Minor — occasionally felt at the surface; no damage.';
  return 'Micro — generally not felt, recorded only by instruments.';
}

/** How this magnitude "compares" in everyday terms, for the interpretation layer. */
export function energyCompare(mag: number): string {
  const j = energyJoules(mag);
  const tntTonnes = j / 4.184e9;
  if (tntTonnes < 0.001) return 'about the energy of a lightning strike';
  if (tntTonnes < 1) return 'a few sticks of dynamite';
  if (tntTonnes < 30) return 'roughly a large quarry blast';
  if (tntTonnes < 1000) return 'a small tactical explosion';
  if (tntTonnes < 20000) return 'comparable to the Hiroshima bomb';
  if (tntTonnes < 1e6) return 'many times the Hiroshima bomb';
  if (tntTonnes < 1e8) return 'in the range of the largest nuclear tests';
  return 'more energy than any weapon ever built';
}

/**
 * Coarse geographic region for a coordinate — a readable grouping for the
 * regions leaderboard when a tectonic plate is not the axis of interest.
 */
export function coarseRegion(lat: number, lon: number): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return 'Unknown';
  const L = ((lon + 180) % 360 + 360) % 360 - 180; // normalise to [-180,180)
  // Specific regions first, general ones last.
  if (lat >= 25 && lat < 46 && L >= 122 && L <= 150) return 'Japan & Korea';
  if (lat >= -12 && lat < 28 && L >= 92 && L <= 145) return 'Southeast Asia';
  if (lat >= -50 && lat < -5 && (L >= 160 || L <= -170)) return 'New Zealand & South Pacific';
  if (lat >= -50 && lat < -5 && L >= 110 && L < 160) return 'Australia & Oceania';
  if (lat >= 50 && L >= -170 && L <= -128) return 'Alaska';
  if (lat >= 50 && (L >= 160 || L <= -160)) return 'Aleutians & Bering';
  if (lat >= 5 && lat < 55 && L >= 55 && L <= 150) return 'Central & East Asia';
  if (lat >= 27 && lat < 50 && L >= -10 && L <= 62) return 'Mediterranean & Middle East';
  if (lat >= 50 && L >= -10 && L <= 62) return 'Northern Europe';
  if (lat >= 5 && lat < 33 && L >= -118 && L <= -58) return 'Central America & Caribbean';
  if (lat >= 15 && lat < 72 && L >= -170 && L <= -52) return 'North America';
  if (lat >= -56 && lat < 15 && L >= -85 && L <= -30) return 'South America';
  if (lat >= -40 && lat < 38 && L >= -20 && L <= 55) return 'Africa';
  if (lat < -55) return 'Antarctica & Southern Ocean';
  return 'Open ocean & other';
}

export interface MagBin {
  /** Lower edge of the bin (inclusive). */
  lo: number;
  hi: number;
  mid: number;
  count: number;
  /** Cumulative count of events with magnitude ≥ lo (Gutenberg–Richter). */
  cum: number;
}

/**
 * Magnitude-frequency bins for the Gutenberg–Richter view. `binSize` defaults to
 * 0.5. Bins span from the floor of the smallest magnitude to the largest.
 */
export function magFrequency(quakes: Quake[], binSize = 0.5): MagBin[] {
  const mags = quakes.map((q) => q.mag).filter((m) => Number.isFinite(m));
  if (mags.length === 0) return [];
  const lo = Math.floor(Math.min(...mags) / binSize) * binSize;
  const hi = Math.ceil((Math.max(...mags) + 1e-9) / binSize) * binSize;
  const bins: MagBin[] = [];
  for (let x = lo; x < hi - 1e-9; x += binSize) {
    bins.push({ lo: round(x), hi: round(x + binSize), mid: round(x + binSize / 2), count: 0, cum: 0 });
  }
  if (bins.length === 0) bins.push({ lo, hi: lo + binSize, mid: lo + binSize / 2, count: 0, cum: 0 });
  for (const m of mags) {
    let idx = Math.floor((m - lo) / binSize);
    if (idx < 0) idx = 0;
    if (idx >= bins.length) idx = bins.length - 1;
    bins[idx].count++;
  }
  // Cumulative ≥ lo edge.
  let running = 0;
  for (let i = bins.length - 1; i >= 0; i--) {
    running += bins[i].count;
    bins[i].cum = running;
  }
  return bins;
}

/**
 * Aki (1965) maximum-likelihood b-value estimate. `mc` is the magnitude of
 * completeness; if omitted, the modal magnitude bin is used as a proxy. Returns
 * NaN when there is not enough data above completeness.
 */
export function bValue(quakes: Quake[], binSize = 0.1, mc?: number): number {
  const mags = quakes.map((q) => q.mag).filter((m) => Number.isFinite(m));
  if (mags.length < 5) return NaN;
  const completeness = mc ?? completenessMagnitude(quakes, 0.5);
  const above = mags.filter((m) => m >= completeness - 1e-9);
  if (above.length < 5) return NaN;
  const mean = above.reduce((a, b) => a + b, 0) / above.length;
  const denom = mean - (completeness - binSize / 2);
  if (denom <= 0) return NaN;
  return Math.log10(Math.E) / denom;
}

/** Magnitude of completeness proxy = midpoint of the most populated bin (the mode). */
export function completenessMagnitude(quakes: Quake[], binSize = 0.5): number {
  const bins = magFrequency(quakes, binSize);
  if (bins.length === 0) return 0;
  let best = bins[0];
  for (const b of bins) if (b.count > best.count) best = b;
  return best.lo;
}

export interface DepthBin {
  lo: number;
  hi: number;
  count: number;
}

/** Histogram bins for a depth distribution (km). */
export function depthHistogram(quakes: Quake[], edges: number[]): DepthBin[] {
  const bins: DepthBin[] = [];
  for (let i = 0; i < edges.length - 1; i++) bins.push({ lo: edges[i], hi: edges[i + 1], count: 0 });
  for (const q of quakes) {
    const d = q.depth;
    if (!Number.isFinite(d)) continue;
    for (let i = 0; i < bins.length; i++) {
      const last = i === bins.length - 1;
      if (d >= bins[i].lo && (d < bins[i].hi || (last && d <= bins[i].hi))) {
        bins[i].count++;
        break;
      }
    }
  }
  return bins;
}

export interface TimeBucket {
  start: number;
  end: number;
  count: number;
  maxMag: number;
  energy: number;
}

/** Evenly divide [start,end] into `n` buckets and tally events + energy. */
export function timeBuckets(quakes: Quake[], n: number, start: number, end: number): TimeBucket[] {
  const buckets: TimeBucket[] = [];
  if (!(end > start) || n < 1) return buckets;
  const span = (end - start) / n;
  for (let i = 0; i < n; i++) {
    buckets.push({ start: start + i * span, end: start + (i + 1) * span, count: 0, maxMag: -Infinity, energy: 0 });
  }
  for (const q of quakes) {
    if (!Number.isFinite(q.time)) continue;
    let idx = Math.floor((q.time - start) / span);
    if (idx < 0) idx = 0;
    if (idx >= n) idx = n - 1;
    buckets[idx].count++;
    buckets[idx].maxMag = Math.max(buckets[idx].maxMag, q.mag);
    buckets[idx].energy += energyJoules(q.mag);
  }
  for (const b of buckets) if (!Number.isFinite(b.maxMag)) b.maxMag = 0;
  return buckets;
}

/** Group + summarise events by an arbitrary key function. */
export interface Group {
  key: string;
  count: number;
  maxMag: number;
  energy: number;
  felt: number;
  quakes: Quake[];
}

export function groupBy(quakes: Quake[], keyFn: (q: Quake) => string): Group[] {
  const map = new Map<string, Group>();
  for (const q of quakes) {
    const key = keyFn(q);
    let g = map.get(key);
    if (!g) {
      g = { key, count: 0, maxMag: -Infinity, energy: 0, felt: 0, quakes: [] };
      map.set(key, g);
    }
    g.count++;
    g.maxMag = Math.max(g.maxMag, q.mag);
    g.energy += energyJoules(q.mag);
    g.felt += q.felt;
    g.quakes.push(q);
  }
  const out = [...map.values()];
  for (const g of out) if (!Number.isFinite(g.maxMag)) g.maxMag = 0;
  return out.sort((a, b) => b.count - a.count);
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}
